// =============================================================================
// RouteManagementScreen
//
// Standalone admin screen for viewing and managing all routes across all
// locations.  Follows the same thin-screen pattern as PerfectTruckManagement:
//   – AdminBreadcrumb + page title
//   – Search / filter client-side
//   – LHDataTable with inline route-type dropdown and active toggle
// =============================================================================

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  Modal,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import FontFamily from '../../../../constants/fonts';
import { AdminColors } from '../../constants/adminColors';
import AdminBreadcrumb from '../../components/AdminBreadcrumb';
import AdminButton from '../../components/AdminButton';
import RouteDataTable from './RouteDataTable';
import BulkCsvUploadModal from './BulkCsvUploadModal';
import AddRouteModal from '../LocationManagement/AddRouteModal';
import { useToast } from '../../../../components/LhToastContainer';
import type { RootState } from '../../../../store';

import {
  fetchRoutes,
  updateRouteType,
  toggleRouteActive,
  fetchActiveRouteTypes,
} from './routeManagementApi';
import { Route, RouteManagementScreenProps, ROUTE_TYPE_OPTIONS } from './routeManagementTypes';

// ─── Truncate long labels ─────────────────────────────────────────────────────

const truncateLabel = (label: string, max = 20, truncatedLen = 15): string =>
  label.length > max ? `${label.substring(0, truncatedLen)}...` : label;

// ─── Compact inline select for table cells ────────────────────────────────────

interface InlineSelectProps {
  value: string | null;
  options: { label: string; value: string }[];
  onChange: (val: string) => void;
  disabled?: boolean;
}

const InlineSelect: React.FC<InlineSelectProps> = ({ value, options, onChange, disabled }) => {
  const displayValue = value ?? '';
  
  if (Platform.OS === 'web') {
    const Tag = 'select' as any;
    const Opt = 'option' as any;
    return (
      <Tag
        value={displayValue}
        onChange={(e: any) => onChange(e.target.value)}
        disabled={disabled}
        style={inlineSelectWebStyle}
      >
        <Opt value="">-- Select --</Opt>
        {options.map(o => (
          <Opt key={o.value} value={o.value}>{truncateLabel(o.label)}</Opt>
        ))}
      </Tag>
    );
  }

  // Native fallback: cycle through options on press
  const idx = options.findIndex(o => o.value === displayValue);
  const label = idx >= 0 ? truncateLabel(options[idx].label) : '-- Select --';

  return (
    <TouchableOpacity
      style={styles.nativeSelect}
      onPress={() => {
        if (disabled) return;
        const next = options[(idx + 1) % options.length];
        onChange(next.value);
      }}
      activeOpacity={0.7}
      disabled={disabled}
    >
      <Text style={styles.nativeSelectText}>{label}</Text>
      <Text style={styles.nativeArrow}>▾</Text>
    </TouchableOpacity>
  );
};

const inlineSelectWebStyle: Record<string, any> = {
  height: 28,
  paddingLeft: 8,
  paddingRight: 8,
  paddingTop: 2,
  paddingBottom: 2,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: AdminColors.borderDefault,
  borderRadius: 4,
  fontSize: 12,
  color: AdminColors.textPrimary,
  backgroundColor: AdminColors.bgSurface,
  cursor: 'pointer',
  appearance: 'auto',
  fontFamily: FontFamily.regular,
};

// ─── Status toggle switch ────────────────────────────────────────────────────

const StatusToggle: React.FC<{ isActive: boolean; onPress: () => void }> = ({ isActive, onPress }) => (
  <TouchableOpacity onPress={onPress} activeOpacity={0.8}>
    <View style={[styles.toggleContainer, isActive ? styles.toggleActive : styles.toggleInactive]}>
      <View style={[styles.toggleThumb, isActive ? styles.toggleThumbActive : styles.toggleThumbInactive]} />
    </View>
  </TouchableOpacity>
);

// ─── Screen ───────────────────────────────────────────────────────────────────

const RouteManagementScreen: React.FC<RouteManagementScreenProps> = ({ onBack, onViewLocationDetail }) => {
  const toast = useToast();
  const requestedBy = useSelector((state: RootState) =>
    state.appData?.userInfo?.email ?? (state as any).auth?.user?.email ?? (state as any).auth?.user?.username ?? ''
  );

  const [routes, setRoutes] = useState<Route[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [isFirst, setIsFirst] = useState(true);
  const [isLast, setIsLast] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedLocationForDetail, setSelectedLocationForDetail] = useState<string>('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
  const [bulkUploadVisible, setBulkUploadVisible] = useState(false);
  const [addRouteModalVisible, setAddRouteModalVisible] = useState(false);
  const [routeTypeOptions, setRouteTypeOptions] = useState<{ label: string; value: string }[]>(ROUTE_TYPE_OPTIONS);
  const ROUTE_PAGE_SIZE = 20;

  // Load route type options from API once on mount; fall back to static list on error
  useEffect(() => {
    fetchActiveRouteTypes()
      .then(opts => { if (opts.length > 0) setRouteTypeOptions(opts); })
      .catch(() => {});
  }, []);

  // ── API load params – drives the useEffect fetch ───────────────────────────
  const [loadParams, setLoadParams] = useState({
    page: 0,
    size: ROUTE_PAGE_SIZE,
    sort: 'locationId,asc',
    searchTerm: '',
    routeTypes: undefined as string[] | undefined,
    isActiveList: undefined as boolean[] | undefined,
  });
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Load (fires whenever loadParams changes) ──────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setApiError('');
      try {
        const response = await fetchRoutes(loadParams);
        if (!cancelled) {
          setRoutes(response.data);
          setCurrentPage(response.currentPage);
          setTotalPages(response.totalPages);
          setPageSize(response.pageSize || loadParams.size);
          setTotalCount(response.totalCount || 0);
          setIsFirst(response.isFirst);
          setIsLast(response.isLast);
        }
      } catch (e: any) {
        if (!cancelled) setApiError(e?.response?.data?.message ?? 'Failed to load routes.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadParams]);

  // API now handles all filtering (multi-routeType and multi-isActive natively)
  const filtered = routes;

  // ── Stats summary (current page only) ────────────────────────────────────────
  const uniqueLocations = useMemo(
    () => new Set(filtered.map(r => r.locationName)).size,
    [filtered],
  );
  const activeCount = useMemo(() => filtered.filter(r => r.isActive).length, [filtered]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const hasActiveFilters = !!searchText || Object.values(columnFilters).some(v => v.length > 0);

  // ── Handlers ─────────────────────────────────────────────────────────────

  // Search: debounce 350 ms then send to API
  const handleSearchChange = (text: string) => {
    setSearchText(text);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setLoadParams(prev => ({ ...prev, searchTerm: text.trim(), page: 0 }));
    }, 350);
  };

  // Map multi-select column filter UI state → typed API arrays
  const deriveApiFilters = (filters: Record<string, string[]>) => ({
    routeTypes: (filters['routeType'] ?? []).length > 0 ? filters['routeType'] : undefined,
    isActiveList: (filters['isActive'] ?? []).length > 0
      ? (filters['isActive'].map(v => v === 'true') as boolean[])
      : undefined,
  });

  const handleColumnFilter = (key: string, values: string[]) => {
    const newFilters = { ...columnFilters, [key]: values };
    setColumnFilters(newFilters);
    setLoadParams(prev => ({ ...prev, ...deriveApiFilters(newFilters), page: 0 }));
  };

  // Sort: server-side – API format: "fieldName,asc" / "fieldName,desc"
  const handleSortChange = (key: string, direction: 'asc' | 'desc') => {
    setLoadParams(prev => ({ ...prev, sort: `${key},${direction}`, page: 0 }));
  };

  const handleClearAllFilters = () => {
    setSearchText('');
    setColumnFilters({});
    setLoadParams(prev => ({
      ...prev,
      searchTerm: '',
      routeTypes: undefined,
      isActiveList: undefined,
      page: 0,
    }));
  };

  const exportTemplate = () => {
    if (Platform.OS !== 'web') return;
    const content =
      'Location_ID,Location_Name,Route_Number,Display_Order,Location_Type,Organization_Type,Is_3PL,Is_Active,SAP_ID,Route_Type,Created_At,Created_By,Updated_At,Updated_By\n' +
      'LOC001,Sample Location,ROUTE100,1,DC,PBNA,false,true,SAP001,LG Delivery,,,, \n';
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'route-upload-template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleRouteTypeChange = async (id: number, newType: string) => {
    const prev = routes.find(r => r.id === id);
    if (!prev || prev.routeType === newType) return;
    // Optimistic update
    setRoutes(cur => cur.map(r => r.id === id ? { ...r, routeType: newType } : r));
    try {
      const updated = await updateRouteType(id, prev.locationId, newType, requestedBy);
      setRoutes(cur => cur.map(r => r.id === id ? updated : r));
      toast.showToast({ header: 'Success', subtext: 'Route type updated.', variant: 'success' });
    } catch (e: any) {
      // Revert
      setRoutes(cur => cur.map(r => r.id === id ? prev : r));
      toast.showToast({ header: 'Error', subtext: e?.response?.data?.message ?? 'Failed to update route type.', variant: 'error' });
    }
  };

  const handleToggleActive = async (id: number) => {
    const prev = routes.find(r => r.id === id);
    if (!prev) return;
    const next = !prev.isActive;
    // Optimistic update
    setRoutes(cur => cur.map(r => r.id === id ? { ...r, isActive: next } : r));
    try {
      const updated = await toggleRouteActive(id, prev.locationId, next, requestedBy);
      setRoutes(cur => cur.map(r => r.id === id ? updated : r));
      toast.showToast({
        header: 'Success',
        subtext: `Route ${prev.routeNumber} ${next ? 'activated' : 'deactivated'}.`,
        variant: 'success',
      });
    } catch (e: any) {
      // Revert
      setRoutes(cur => cur.map(r => r.id === id ? prev : r));
      toast.showToast({ header: 'Error', subtext: e?.response?.data?.message ?? 'Failed to update route.', variant: 'error' });
    }
  };

  // ── Table columns ────────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        columnKey: 'routeNumber',
        header: 'Route Number',
        flexGrow: 1.5,
        sortable: true,
      },
      {
        columnKey: 'routeType',
        header: 'Route Type',
        flexGrow: 1.8,
        sortable: true,
        filterOptions: routeTypeOptions,
        renderCell: (item: Route) => (
          <InlineSelect
            value={item.routeType}
            options={routeTypeOptions}
            onChange={val => handleRouteTypeChange(item.id, val)}
          />
        ),
      },
      {
        columnKey: 'locationName',
        header: 'Location Name',
        flexGrow: 2.8,
        sortable: true,
        renderCell: (item: Route) => (
          <TouchableOpacity
            onPress={() => {
              setSelectedLocationForDetail(item.locationName);
              setModalVisible(true);
            }}
            activeOpacity={0.7}
          >
            <Text style={{ fontFamily: FontFamily.bold, color: AdminColors.blue700}}>
              {item.locationName}
            </Text>
          </TouchableOpacity>
        ),
      },
      {
        columnKey: 'locationId',
        header: 'Location ID',
        flexGrow: 1.5,
        sortable: true,
        renderCell: (item: Route) => (
          <Text style={{ fontFamily: FontFamily.regular, color: AdminColors.textPrimary }}>
            {item.locationId}
          </Text>
        ),
      },
      {
        columnKey: 'sapId',
        header: 'SAP ID',
        flexGrow: 1.5,
        sortable: true,
        renderCell: (item: Route) => (
          <Text style={{ fontFamily: FontFamily.regular, color: AdminColors.textPrimary }}>
            {item.sapId ?? '—'}
          </Text>
        ),
      },
      {
        columnKey: 'isActive',
        header: 'Active',
        flexGrow: 1,
        sortable: false,
        filterOptions: [
          { label: 'Active', value: 'true' },
          { label: 'Inactive', value: 'false' },
        ],
        renderCell: (item: Route) => (
          <StatusToggle
            isActive={item.isActive}
            onPress={() => handleToggleActive(item.id)}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [routes, routeTypeOptions],
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <View style={styles.wrapper}>
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <View style={styles.header}>
        <AdminBreadcrumb
          items={[
            { label: 'Configurations', onPress: onBack },
            { label: 'Route Management' },
          ]}
        />
        <View style={styles.headerRow}>
          <View style={styles.headerLeft}>
            <Text style={styles.pageTitle}>Route Management</Text>
            <Text style={styles.pageSubtitle}>All routes across all locations</Text>
          </View>
        </View>
      </View>

      {/* ── Body ────────────────────────────────────────────────────────────── */}
      <ScrollView
        style={styles.body}
        contentContainerStyle={styles.bodyContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Error banner */}
        {!!apiError && (
          <View style={styles.errorBanner}>
            <MaterialIcons name="error-outline" size={16} color={AdminColors.red500} style={{ marginRight: 8 }} />
            <Text style={styles.errorText}>{apiError}</Text>
          </View>
        )}

        {/* Search + stats bar */}
        <View style={styles.toolbar}>
          <View style={styles.toolbarLeft}>
            <View style={styles.searchBox}>
              <MaterialIcons name="search" size={16} color={AdminColors.textMuted} style={{ marginRight: 6 }} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search routes, locations…"
                placeholderTextColor={AdminColors.textMuted}
                value={searchText}
                onChangeText={handleSearchChange}
              />
              {!!searchText && (
                <TouchableOpacity onPress={() => handleSearchChange('')} activeOpacity={0.7}>
                  <MaterialIcons name="close" size={14} color={AdminColors.textMuted} />
                </TouchableOpacity>
              )}
            </View>
            {!isLoading && (
              <Text style={styles.countText}>
                Showing {filtered.length} routes on this page
                {uniqueLocations > 0 ? ` across ${uniqueLocations} location${uniqueLocations !== 1 ? 's' : ''}` : ''}
              </Text>
            )}
          </View>
          <View style={styles.toolbarRight}>
            {hasActiveFilters && (
              <AdminButton
                label="Clear Filters"
                variant="secondary"
                small
                onPress={handleClearAllFilters}
              />
            )}
            <AdminButton
              label="CSV Template Export"
              variant="secondary"
              onPress={exportTemplate}
            />
            <AdminButton
              label="+ Add Route"
              variant="secondary"
              onPress={() => setAddRouteModalVisible(true)}
            />
            <AdminButton
              label="Bulk CSV Upload"
              variant="primary"
              onPress={() => setBulkUploadVisible(true)}
            />
          </View>
        </View>

        {/* Stats chips */}
        {!isLoading && routes.length > 0 && (
          <View style={styles.statsRow}>
            <View style={[styles.chip, { backgroundColor: AdminColors.blue75 }]}>
              <Text style={[styles.chipText, { color: AdminColors.blue900 }]}>
                {routes.length} Routes on Page (Total: {totalCount})
              </Text>
            </View>
            <View style={[styles.chip, { backgroundColor: AdminColors.green75 }]}>
              <Text style={[styles.chipText, { color: AdminColors.green600 }]}>{activeCount} Active</Text>
            </View>
            {uniqueLocations > 0 && (
              <View style={[styles.chip, { backgroundColor: AdminColors.tagTeal.bg }]}>
                <Text style={[styles.chipText, { color: AdminColors.tagTeal.text }]}>{uniqueLocations} Locations</Text>
              </View>
            )}
          </View>
        )}

        {/* Table */}
        <View style={styles.surface}>
          <RouteDataTable
            data={filtered}
            columns={columns}
            tableHeader=""
            tableDescription={`Showing ${currentPage * pageSize + 1}–${Math.min((currentPage + 1) * pageSize, totalCount)} of ${totalCount}`}
            showHeaderTopBorder={false}
            onRowClick={() => {}}
            isLoading={isLoading}
            maxRecordsToShow={pageSize}
            externalPage={currentPage}
            externalTotalPages={totalPages}
            onExternalPageChange={(newPage) => setLoadParams(prev => ({ ...prev, page: newPage }))}
            onPageSizeChange={(newSize) => setLoadParams(prev => ({ ...prev, size: newSize, page: 0 }))}
            onSort={handleSortChange}
            isFirstPage={isFirst}
            isLastPage={isLast}
            columnFilters={columnFilters}
            onColumnFilter={handleColumnFilter}
          />
        </View>
      </ScrollView>

      {/* ── Navigate to Location Details Modal ──────────────────────────────── */}
      <Modal
        visible={modalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            {/* Header with close button */}
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Navigate to Location Details</Text>
              <TouchableOpacity
                onPress={() => setModalVisible(false)}
                activeOpacity={0.7}
              >
                <Text style={styles.modalCloseButton}>×</Text>
              </TouchableOpacity>
            </View>

            {/* Modal body */}
            <View style={styles.modalBody}>
              <Text style={styles.modalMessage}>
                You will now be navigated to Location Details for <Text style={styles.modalLocationName}>{selectedLocationForDetail}</Text>.
              </Text>
            </View>

            {/* Buttons */}
            <View style={styles.modalButtonRow}>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonCancel]}
                onPress={() => setModalVisible(false)}
                activeOpacity={0.7}
              >
                <Text style={styles.modalButtonCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonOk]}
                onPress={() => {
                  setModalVisible(false);
                  onViewLocationDetail?.(selectedLocationForDetail);
                }}
                activeOpacity={0.7}
              >
                <Text style={styles.modalButtonOkText}>OK</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Bulk CSV Upload Modal ────────────────────────────────────────────── */}
      <BulkCsvUploadModal
        visible={bulkUploadVisible}
        onClose={() => setBulkUploadVisible(false)}
        onSuccess={() => {
          setBulkUploadVisible(false);
          setLoadParams(prev => ({ ...prev, page: 0 }));
        }}
      />

      {/* ── Add Route Modal ──────────────────────────────────────────────────── */}
      <AddRouteModal
        visible={addRouteModalVisible}
        onClose={() => setAddRouteModalVisible(false)}
        onSuccess={() => {
          setAddRouteModalVisible(false);
          setLoadParams(prev => ({ ...prev, page: 0 }));
        }}
        routeTypeOptions={routeTypeOptions}
      />
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    backgroundColor: AdminColors.bgPage,
  },
  header: {
    paddingHorizontal: 32,
    paddingTop: 32,
    paddingBottom: 16,
    backgroundColor: AdminColors.bgPage,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  headerLeft: { flex: 1 },
  pageTitle: {
    fontSize: 28,
    fontFamily: FontFamily.bold,
    color: AdminColors.textPrimary,
    lineHeight: 40,
    marginTop: 4,
  },
  pageSubtitle: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: AdminColors.textMuted,
    marginTop: 2,
  },

  // ── Body
  body: { flex: 1 },
  bodyContent: {
    paddingHorizontal: 32,
    paddingBottom: 60,
  },

  // ── Error
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AdminColors.red75,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.red500,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginBottom: 16,
  },
  errorText: {
    flex: 1,
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.red500,
  },

  // ── Toolbar
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
    gap: 12,
    flexWrap: 'wrap',
  },
  toolbarLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    flex: 1,
    minWidth: 200,
  },
  toolbarRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 36,
    paddingHorizontal: 12,
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    minWidth: 240,
    maxWidth: 340,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: AdminColors.textPrimary,
    paddingVertical: 0,
    outlineWidth: 0,
  } as any,
  countText: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: AdminColors.textMuted,
  },

  // ── Stats chips
  statsRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 4,
  },
  chipText: {
    fontSize: 11,
    fontFamily: FontFamily.medium,
  },

  // ── Table surface
  surface: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    overflow: 'hidden',
    ...Platform.select({
      web: { boxShadow: '0 0 24px rgba(0,94,192,0.08)' } as any,
      default: {
        shadowColor: '#005EC0',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.08,
        shadowRadius: 24,
        elevation: 2,
      },
    }),
  },

  // ── Empty state
  emptyWrapper: {
    paddingVertical: 64,
    alignItems: 'center',
    gap: 8,
  },
  emptyTitle: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    color: AdminColors.textPrimary,
    marginTop: 8,
  },
  emptySubtitle: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: AdminColors.textMuted,
  },

  // ── Inline select (native)
  nativeSelect: {
    height: 28,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AdminColors.bgSurface,
    minWidth: 110,
  },
  nativeSelectText: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: AdminColors.textPrimary,
    flex: 1,
  },
  nativeArrow: {
    fontSize: 10,
    color: AdminColors.textMuted,
    marginLeft: 4,
  },

  // ── Status toggle
  toggleContainer: {
    width: 36,
    height: 20,
    borderRadius: 10,
    padding: 2,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  toggleActive: {
    backgroundColor: AdminColors.green400,
  },
  toggleInactive: {
    backgroundColor: '#E0E0E0',
  },
  toggleThumb: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleThumbInactive: {
    alignSelf: 'flex-start',
  },

  // ── Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 12,
    width: '90%',
    maxWidth: 480,
    overflow: 'hidden',
    ...Platform.select({
      web: { boxShadow: '0 8px 32px rgba(0,0,0,0.15)' } as any,
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.15,
        shadowRadius: 16,
        elevation: 8,
      },
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
  },
  modalTitle: {
    fontSize: 20,
    fontFamily: FontFamily.bold,
    color: AdminColors.textPrimary,
  },
  modalCloseButton: {
    fontSize: 32,
    color: AdminColors.textMuted,
    marginTop: -6,
    lineHeight: 24,
  },
  modalBody: {
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  modalMessage: {
    fontSize: 16,
    fontFamily: FontFamily.regular,
    color: AdminColors.textPrimary,
    lineHeight: 24,
  },
  modalLocationName: {
    fontFamily: FontFamily.bold,
    color: AdminColors.textPrimary,
  },
  modalButtonRow: {
    flexDirection: 'row',
    gap: 16,
    paddingHorizontal: 24,
    paddingBottom: 24,
    justifyContent: 'flex-end',
  },
  modalButton: {
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 28,
    minWidth: 120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalButtonCancel: {
    borderWidth: 2,
    borderColor: AdminColors.blue700,
    backgroundColor: '#FFFFFF',
  },
  modalButtonCancelText: {
    fontSize: 15,
    fontFamily: FontFamily.bold,
    color: AdminColors.blue700,
  },
  modalButtonOk: {
    backgroundColor: AdminColors.blue700,
  },
  modalButtonOkText: {
    fontSize: 15,
    fontFamily: FontFamily.bold,
    color: '#FFFFFF',
  },
});

export default RouteManagementScreen;
